import React, { useMemo } from 'react';
import { CheckIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx'; // Optional for cleaner class handling

type ChecklistStrengthProps = {
  title?: string;
  options: string[];
  progressBarColor?: string;
  checkIcon?: JSX.Element;
  completedOptions?: string[];
  onOptionChange?: (option: string, remove?: boolean) => void;
  disableClick?: boolean;
  showBar?: boolean;
  onClick?: (task: string) => void;
};

const ChecklistStrength: React.FC<ChecklistStrengthProps> = ({
  title = 'Checklist',
  options,
  progressBarColor = 'bg-green-500',
  checkIcon = <CheckIcon className="w-4 h-4 font-medium text-white" />,
  completedOptions = [],
  onOptionChange,
  disableClick = true,
  showBar = true,
  onClick,
}) => {
  // Memoize progress calculation to improve performance.
  const progress = useMemo(
    () => (options.length > 0 ? (completedOptions.length / options.length) * 100 : 0),
    [completedOptions, options]
  );

  const handleOptionChange = (option: string) => {
    if (onOptionChange) {
      const isCompleted = completedOptions.includes(option);
      onOptionChange(option, isCompleted); // Pass remove flag based on inclusion
    }
  };

  return (
    <div className="w-full dark:bg-darkBg ">
      <div className="flex flex-col space-y-2">
        <div className="flex items-center justify-between font-semibold">
          <h2>{title}</h2>
          <h4>{`${progress.toFixed(0)}%`}</h4> {/* Ensure progress is rounded */}
        </div>

        {showBar && (
          <div className="py-1">
            <div className="h-4 bg-gray-200 dark:bg-darkBg dark:border dark:border-darkBg-border rounded-md">
              <div
                className={clsx('h-full rounded-md', progressBarColor)}
                style={{ width: `${progress}%` }}
                role="progressbar"
                aria-valuenow={progress}
                aria-valuemin={0}
                aria-valuemax={100}
              ></div>
            </div>
          </div>
        )}

        {options.map((option, index) => (
          <label
            key={index}
            className="flex items-center space-x-2 cursor-pointer"
            onClick={() => (onClick?.(option))} // Prevent click if disabled
          >
            <input
              type="checkbox"
              className="sr-only"
              checked={completedOptions.includes(option)}
              onChange={() => handleOptionChange(option)}
              disabled={disableClick}
              aria-checked={completedOptions.includes(option)}
            />
            <span
              className={clsx(
                'text-gray-700 dark:text-darkBg-mainText size-5 border border-gray-300 dark:border dark:border-darkBg-border rounded-full flex items-center justify-center shrink-0 p-1',
                completedOptions.includes(option) && 'bg-[#069803] text-white font-bold'
              )}
            >
              {completedOptions.includes(option) && checkIcon}
            </span>
            <span className="text-gray-700 dark:text-darkBg-mainText">{option}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

export default ChecklistStrength;
